import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

/**
 * Auth Callback Page
 * Handles OAuth redirects from Supabase
 * 
 * This version manually stores the session without using Supabase client methods
 * to avoid the "body stream already read" error.
 */
const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing authentication...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for error in URL
        const errorParam = searchParams.get('error');
        if (errorParam) {
          setError(searchParams.get('error_description') || errorParam);
          return;
        }

        const hash = window.location.hash;
        
        if (!hash || !hash.includes('access_token')) {
          setError('No authentication data found. Please try again.');
          return;
        }

        setStatus('Processing tokens...');
        
        // Parse the hash manually
        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const expiresAt = hashParams.get('expires_at');
        const expiresIn = hashParams.get('expires_in');
        const tokenType = hashParams.get('token_type');
        const providerToken = hashParams.get('provider_token');
        const providerRefreshToken = hashParams.get('provider_refresh_token');
        
        if (!accessToken) {
          setError('No access token received.');
          return;
        }

        console.log('Access token received, length:', accessToken.length);
        
        setStatus('Decoding user information...');
        
        // Decode the JWT to get user info (JWT is base64 encoded)
        let user = null;
        try {
          const tokenParts = accessToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('Token payload:', payload);
            
            user = {
              id: payload.sub,
              email: payload.email,
              user_metadata: payload.user_metadata || {},
              app_metadata: payload.app_metadata || {},
              aud: payload.aud,
              role: payload.role
            };
          }
        } catch (decodeErr) {
          console.error('Error decoding token:', decodeErr);
        }

        if (!user) {
          setError('Could not decode user information from token.');
          return;
        }

        setStatus('Saving session...');
        
        // Get the Supabase project URL from env
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const storageKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
        
        // Create session object in Supabase format
        const session = {
          access_token: accessToken,
          refresh_token: refreshToken || '',
          expires_at: parseInt(expiresAt) || Math.floor(Date.now() / 1000) + parseInt(expiresIn || 3600),
          expires_in: parseInt(expiresIn) || 3600,
          token_type: tokenType || 'bearer',
          user: user,
          provider_token: providerToken,
          provider_refresh_token: providerRefreshToken
        };
        
        // Store in localStorage (this is how Supabase persists sessions)
        localStorage.setItem(storageKey, JSON.stringify(session));
        
        // Also store our app-specific flags
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userEmail', user.email || '');
        if (user.user_metadata?.full_name) {
          localStorage.setItem('userName', user.user_metadata.full_name);
        }
        
        console.log('Session stored successfully for:', user.email);
        
        setStatus('Success! Redirecting to dashboard...');
        
        // Small delay to show success message
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Redirect to dashboard using full page navigation to reinitialize Supabase client
        window.location.href = '/dashboard';
        
      } catch (err) {
        console.error('Callback exception:', err);
        setError(`Error: ${err.message}`);
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: 'var(--bg-primary)', paddingTop: '80px' }}
      >
        <div className="text-center max-w-md">
          <div className="mb-4 text-red-500">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="heading-2 mb-2">Authentication Error</h1>
          <p className="body-regular mb-6" style={{ color: 'var(--text-secondary)' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary px-6 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="text-center">
        <Loader2 className="animate-spin mx-auto mb-4" size={48} style={{ color: 'var(--olive-primary)' }} />
        <h1 className="heading-2 mb-2">Signing You In</h1>
        <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
          {status}
        </p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
